import React, { useState, useMemo } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    InputAdornment,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import DescriptionIcon from '@material-ui/icons/Description'
import DeleteIcon from '@material-ui/icons/Delete'
import VisibilityIcon from '@material-ui/icons/Visibility'
import ShareIcon from '@material-ui/icons/Share'
import RefreshIcon from '@material-ui/icons/Refresh'
import SearchIcon from '@material-ui/icons/Search'
import EditIcon from '@material-ui/icons/Edit'
import { toast } from 'react-toastify'
import {
    ContractStatusChip,
    lifecycleRowClassName,
    lifecycleRowShadeStyles,
} from '../ContractManager/ContractStatusChip'
import {
    ListVendor,
    ApartmentContract,
    ContractPaymentTerm,
} from '../../models/Company/types'
import { useVendorContracts } from '../../hooks/useVendorContracts'
import { updateContractRequest } from '../../hooks/useCompanyContracts'
import { DropFiles } from '../../components/DropFiles/DropFiles'
import { ContractViewModal } from '../ContractManager/ContractViewModal'
import { DeleteConfirmDialog } from '../ContractManager/DeleteConfirmDialog'

interface VendorContractsModalProps {
    isOpen: boolean
    onClose: () => void
    vendor: ListVendor
}

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        borderRadius: theme.spacing(1.5),
        height: '90vh',
        maxHeight: '90vh',
        width: '95vw',
        maxWidth: '1400px',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    iconContainer: {
        height: 44,
        width: 44,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.light + '20',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.main,
    },
    title: {
        fontSize: '1.125rem',
        fontWeight: 600,
    },
    subtitle: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
    },
    content: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        height: 'calc(85vh - 130px)',
        overflow: 'hidden',
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    searchField: {
        minWidth: 300,
    },
    tableWrapper: {
        flex: 1,
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    tableContainer: {
        flex: 1,
        overflow: 'auto',
    },
    table: {
        minWidth: 650,
    },
    headerRow: {
        backgroundColor: theme.palette.grey[100],
    },
    headerCell: {
        fontWeight: 600,
        color: theme.palette.text.secondary,
        fontSize: '13px',
    },
    row: {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    ...lifecycleRowShadeStyles,
    iconCell: {
        width: 50,
    },
    contractIcon: {
        height: 32,
        width: 32,
        borderRadius: theme.spacing(0.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.primary.light + '20',
        color: theme.palette.primary.main,
    },
    nameCell: {
        fontWeight: 500,
    },
    cellText: {
        fontSize: '14px',
    },
    summaryCell: {
        color: theme.palette.text.secondary,
        fontSize: '14px',
        maxWidth: 200,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    analyzingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    analyzingText: {
        fontSize: '14px',
        fontStyle: 'italic',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    actionButton: {
        padding: theme.spacing(1),
    },
    shareButton: {
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light + '20',
        },
    },
    viewButton: {
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light + '20',
        },
    },
    reanalyzeButton: {
        color: theme.palette.secondary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.light + '20',
        },
    },
    deleteButton: {
        color: theme.palette.text.secondary,
        '&:hover': {
            color: theme.palette.error.main,
            backgroundColor: theme.palette.error.light + '20',
        },
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(6),
        backgroundColor: theme.palette.grey[50],
    },
    emptyText: {
        color: theme.palette.text.secondary,
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    pagination: {
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    dialogActions: {
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}))

// Allowed file types for DropFiles
const allowedFileTypes = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
        '.docx',
    ],
    'text/plain': ['.txt'],
}

// Payment term display labels
const PAYMENT_TERM_LABELS: Record<ContractPaymentTerm, string> = {
    [ContractPaymentTerm.MONTHLY]: 'Monthly',
    [ContractPaymentTerm.QUARTERLY]: 'Quarterly',
    [ContractPaymentTerm.ANNUALLY]: 'Annually',
    [ContractPaymentTerm.ONE_TIME]: 'One-Time',
    [ContractPaymentTerm.PER_JOB]: 'Per Job',
}

export const VendorContractsModal: React.FC<VendorContractsModalProps> = ({
    isOpen,
    onClose,
    vendor,
}) => {
    const classes = useStyles()

    const {
        contracts,
        isLoadingContracts,
        uploadContracts,
        processContract,
        updateContract,
        deleteContract,
        isUploading,
        isUpdating,
        isDeleting,
        processingIds,
    } = useVendorContracts({ vendorId: vendor.id })

    // Search state
    const [searchText, setSearchText] = useState('')

    // Pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    // View modal state
    const [viewModalOpen, setViewModalOpen] = useState(false)
    const [viewStartsEditing, setViewStartsEditing] = useState(false)
    const [
        selectedContract,
        setSelectedContract,
    ] = useState<ApartmentContract | null>(null)
    const viewedContract = useMemo(() => {
        if (!selectedContract) return null
        return (
            contracts.find((item) => item.id === selectedContract.id) ??
            selectedContract
        )
    }, [contracts, selectedContract])

    // Delete confirmation state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [
        contractToDelete,
        setContractToDelete,
    ] = useState<ApartmentContract | null>(null)

    // Filter contracts based on search text
    const filteredContracts = useMemo(() => {
        if (!searchText.trim()) {
            return contracts
        }
        const searchLower = searchText.toLowerCase()
        return contracts.filter((contract) => {
            const nameMatch = contract.name.toLowerCase().includes(searchLower)
            const summaryMatch = contract.ai_summary
                ?.toLowerCase()
                .includes(searchLower)
            return nameMatch || summaryMatch
        })
    }, [contracts, searchText])

    // Paginate filtered contracts
    const paginatedContracts = useMemo(() => {
        const start = page * rowsPerPage
        const end = start + rowsPerPage
        return filteredContracts.slice(start, end)
    }, [filteredContracts, page, rowsPerPage])

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value)
        setPage(0)
    }

    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleUpload = async (files: File[]) => {
        const results = await uploadContracts(files)

        // Process each successfully uploaded contract
        results.forEach((result) => {
            if (result.status === 'fulfilled') {
                const contract = result.value.data
                processContract.mutate(contract.id)
            }
        })
    }

    const handleView = (contract: ApartmentContract) => {
        setSelectedContract(contract)
        setViewStartsEditing(false)
        setViewModalOpen(true)
    }

    const handleCloseViewModal = () => {
        setViewModalOpen(false)
        setSelectedContract(null)
    }

    const handleEdit = (contract: ApartmentContract) => {
        setSelectedContract(contract)
        setViewStartsEditing(true)
        setViewModalOpen(true)
    }

    const handleSaveContract = (
        contractId: number,
        data: updateContractRequest,
    ) => updateContract.mutateAsync({ contractId, data })

    const handleDeleteClick = (contract: ApartmentContract) => {
        setContractToDelete(contract)
        setDeleteDialogOpen(true)
    }

    const handleConfirmDelete = () => {
        if (contractToDelete) {
            deleteContract.mutate(contractToDelete.id, {
                onSuccess: () => {
                    setDeleteDialogOpen(false)
                    setContractToDelete(null)
                },
            })
        }
    }

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false)
        setContractToDelete(null)
    }

    const handleShare = async (contract: ApartmentContract) => {
        try {
            await navigator.clipboard.writeText(contract.file)
            toast.success('Contract link copied to clipboard.')
        } catch (error) {
            // Fallback for browsers that don't support clipboard API
            const textArea = window.document.createElement('textarea')
            textArea.value = contract.file
            window.document.body.appendChild(textArea)
            textArea.select()
            window.document.execCommand('copy')
            window.document.body.removeChild(textArea)
            toast.success('Contract link copied to clipboard.')
        }
    }

    const handleReanalyze = (id: number) => {
        processContract.mutate(id)
    }

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '—'
        // Parse date parts directly to avoid timezone conversion issues
        const [year, month, day] = dateString.split('T')[0].split('-')
        const months = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ]
        return `${months[parseInt(month, 10) - 1]} ${parseInt(
            day,
            10,
        )}, ${year}`
    }

    const formatCurrency = (value: number | null) => {
        if (value === null) return '—'
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value)
    }

    const formatPaymentTerm = (term: ContractPaymentTerm | null) => {
        if (!term) return '—'
        return PAYMENT_TERM_LABELS[term] || '—'
    }

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{ className: classes.paper }}
            >
                {/* Header */}
                <Box className={classes.header}>
                    <Box className={classes.headerLeft}>
                        <Box className={classes.iconContainer}>
                            <DescriptionIcon />
                        </Box>
                        <Box>
                            <Typography className={classes.title}>
                                Contracts
                            </Typography>
                            <Typography className={classes.subtitle}>
                                {vendor.name}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Content */}
                <DialogContent className={classes.content}>
                    <DropFiles
                        onDrop={handleUpload}
                        displayString={
                            isUploading
                                ? 'Uploading...'
                                : 'Drag and drop contract files here (PDF, DOCX, TXT)'
                        }
                        fileTypes={allowedFileTypes}
                        multipleFiles={true}
                    />

                    {/* Search */}
                    <Box className={classes.searchContainer}>
                        <TextField
                            className={classes.searchField}
                            placeholder="Search contracts..."
                            variant="outlined"
                            size="small"
                            value={searchText}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Typography variant="body2" color="textSecondary">
                            {filteredContracts.length} contract
                            {filteredContracts.length !== 1 ? 's' : ''}
                        </Typography>
                    </Box>

                    {/* Contracts Table */}
                    {isLoadingContracts ? (
                        <Box className={classes.loadingContainer}>
                            <CircularProgress />
                        </Box>
                    ) : filteredContracts.length === 0 ? (
                        <Box className={classes.emptyState}>
                            <Typography className={classes.emptyText}>
                                No contracts found for this vendor.
                            </Typography>
                        </Box>
                    ) : (
                        <Box className={classes.tableWrapper}>
                            <TableContainer className={classes.tableContainer}>
                                <Table className={classes.table} stickyHeader>
                                    <TableHead>
                                        <TableRow className={classes.headerRow}>
                                            <TableCell
                                                className={classes.headerCell}
                                                style={{ width: 50 }}
                                            />
                                            <TableCell
                                                className={classes.headerCell}
                                                style={{ width: 200 }}
                                            >
                                                Contract Name
                                            </TableCell>
                                            <TableCell
                                                className={classes.headerCell}
                                                style={{ width: 110 }}
                                            >
                                                Start Date
                                            </TableCell>
                                            <TableCell
                                                className={classes.headerCell}
                                                style={{ width: 110 }}
                                            >
                                                End Date
                                            </TableCell>
                                            <TableCell
                                                className={classes.headerCell}
                                                style={{ width: 140 }}
                                            >
                                                Status
                                            </TableCell>
                                            <TableCell
                                                className={classes.headerCell}
                                                style={{ width: 100 }}
                                            >
                                                Payment
                                            </TableCell>
                                            <TableCell
                                                className={classes.headerCell}
                                                style={{ width: 100 }}
                                            >
                                                Amount
                                            </TableCell>
                                            <TableCell
                                                className={classes.headerCell}
                                            >
                                                Summary
                                            </TableCell>
                                            <TableCell
                                                className={classes.headerCell}
                                                style={{ width: 140 }}
                                            >
                                                Actions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedContracts.map((contract) => (
                                            <TableRow
                                                key={contract.id}
                                                className={lifecycleRowClassName(
                                                    classes,
                                                    contract.lifecycle_status,
                                                )}
                                            >
                                                <TableCell
                                                    className={classes.iconCell}
                                                >
                                                    <Box
                                                        className={
                                                            classes.contractIcon
                                                        }
                                                    >
                                                        <DescriptionIcon
                                                            style={{
                                                                fontSize: 18,
                                                            }}
                                                        />
                                                    </Box>
                                                </TableCell>
                                                <TableCell
                                                    className={classes.nameCell}
                                                >
                                                    <Typography variant="body2">
                                                        {contract.name || '—'}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        {(contract.contract_type ===
                                                        'CERTIFICATE'
                                                            ? contract.organization_certificate_type_detail
                                                            : contract.organization_contract_type_detail
                                                        )?.name || 'Untyped'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    className={classes.cellText}
                                                >
                                                    {formatDate(
                                                        contract.start_date,
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    className={classes.cellText}
                                                >
                                                    {formatDate(
                                                        contract.end_date,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <ContractStatusChip
                                                        status={
                                                            contract.lifecycle_status
                                                        }
                                                        autoRenew={
                                                            contract.auto_renew
                                                        }
                                                        endDate={
                                                            contract.end_date
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    className={classes.cellText}
                                                >
                                                    {formatPaymentTerm(
                                                        contract.payment_term,
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    className={classes.cellText}
                                                >
                                                    {formatCurrency(
                                                        contract.payment_amount,
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    className={
                                                        classes.summaryCell
                                                    }
                                                >
                                                    {processingIds.has(
                                                        contract.id,
                                                    ) ? (
                                                        <Box
                                                            className={
                                                                classes.analyzingContainer
                                                            }
                                                        >
                                                            <CircularProgress
                                                                size={16}
                                                            />
                                                            <Typography
                                                                className={
                                                                    classes.analyzingText
                                                                }
                                                            >
                                                                Analyzing...
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        contract.ai_summary ||
                                                        '—'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        className={
                                                            classes.actions
                                                        }
                                                    >
                                                        <Tooltip title="Share Link">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    handleShare(
                                                                        contract,
                                                                    )
                                                                }
                                                                className={`${classes.actionButton} ${classes.shareButton}`}
                                                            >
                                                                <ShareIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    handleView(
                                                                        contract,
                                                                    )
                                                                }
                                                                className={`${classes.actionButton} ${classes.viewButton}`}
                                                            >
                                                                <VisibilityIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit Contract">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    handleEdit(
                                                                        contract,
                                                                    )
                                                                }
                                                                className={`${classes.actionButton} ${classes.viewButton}`}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Reanalyze">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    handleReanalyze(
                                                                        contract.id,
                                                                    )
                                                                }
                                                                className={`${classes.actionButton} ${classes.reanalyzeButton}`}
                                                                disabled={processingIds.has(
                                                                    contract.id,
                                                                )}
                                                            >
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    handleDeleteClick(
                                                                        contract,
                                                                    )
                                                                }
                                                                className={`${classes.actionButton} ${classes.deleteButton}`}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                className={classes.pagination}
                                component="div"
                                count={filteredContracts.length}
                                page={page}
                                onChangePage={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 25]}
                            />
                        </Box>
                    )}
                </DialogContent>

                {/* Actions */}
                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={onClose}
                        variant="outlined"
                        color="default"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Contract Modal */}
            <ContractViewModal
                isOpen={viewModalOpen}
                onClose={handleCloseViewModal}
                contract={viewedContract}
                initialEditing={viewStartsEditing}
                onSave={handleSaveContract}
                isSaving={isUpdating}
                hideVendorField
            />

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                isOpen={deleteDialogOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                documentName={contractToDelete?.name || ''}
                isDeleting={isDeleting}
            />
        </>
    )
}
